// Auth store + magic-link / Google OAuth / whoami / logout calls.
//
// Backend contract (backend/web/Roles.Web/Endpoints/Public/AuthEndpoints.cs):
//   POST /api/auth/email/request  { email }            → 202 { requestId }
//   POST /api/auth/exchange       { clickToken }       → 200, sets vd_session cookie
//   POST /api/auth/email/cancel   { id }               → 204
//   POST /api/user/logout                              → clears cookie
//   GET  /api/user/whoami         (cookie required)    → CurrentUser
//
// No account has a password. The admin host signs in through Cloudflare Access:
// POST /api/admin/session mints its session, re-called on every 401 there.

import { writable, type Readable, derived, get } from 'svelte/store';
import type {
  CellHandoffResponse,
  CueLength,
  ImpersonationStatusResponse,
  OperationResponse,
  SubscriptionStatus,
  UserId,
} from '@voicedojo/backend-types';
import { SignInBotCheckAction, setUserCulture, setUserTimeZone, SystemKeys } from '@voicedojo/backend-types';
import {
  api,
  apiOrigin,
  ApiError,
  isAccountMoving,
  ON_ADMIN_HOST,
  setAccountMovingListener,
  setCrossToCell,
  setRefreshOn401,
  setGuestGate,
} from '../api/client';
import { cellOrigin, markCellCrossing, writeHomeCookie } from '../api/cellProbe';
import { openAdminSession } from '../api/adminAuth';
import { activeCulture, t, wireCulture, type Culture } from '$lib/i18n';
import { recaptchaToken } from '$lib/recaptcha/recaptcha';
import { accountMove, noteAccountMoving, noteAccountNotMoving, setAccountMoveRecheck } from '$lib/stores/accountMove';
import { deviceTimeZone, setRecordedTimeZone } from '$lib/utils/formatting/timeZone.svelte';

/**
 * Impersonation sidecar from `/api/user/whoami`: WHO REALLY AUTHENTICATED. The
 * whoami body is already the account being acted as, so this names the admin
 * behind it — the two halves never restate each other. Its presence is the only
 * source of truth for "am I impersonating" (the cookie is HttpOnly), and the
 * server resolves it from the admin's write model, not the `act` claim.
 */
export type ImpersonationStatus = ImpersonationStatusResponse;

export type CurrentUser = {
  id: UserId;
  email: string;
  mobile: string;
  roles: string[];
  isActive: boolean;
  /**
   * Single display name. Set either by submitting the name dialog (magic-link
   * path) or by the OAuth callback from the provider's full-name claim. Empty
   * for a freshly signed-up magic-link user who has not yet submitted the
   * dialog. See wiki/feature-user-profile.md AC-UP-1 — the FE uses
   * an empty name (plus signed-in) as the dialog trigger.
   */
  name?: string | null;
  /**
   * Avatar — a bundled stock-avatar id or an uploaded object-storage key. The
   * nav + profile render it. See wiki/feature-user-profile.md AC-UP-3.
   */
  avatar?: string | null;
  /**
   * Preferred speaking rate in words-per-minute (default 150, range 50–250).
   * Drives the profile slider. See wiki/feature-user-profile.md AC-UP-4.
   */
  preferredWpm?: number;
  /** The least a timed cue's effect lasts. */
  cueLength?: CueLength;
  /** Subscription status from the User read model. Defaults to 'None'. */
  subscriptionStatus?: SubscriptionStatus;
  /** ISO date string for the current Stripe period end, when applicable. */
  subscriptionEndsAt?: string | null;
  /**
   * Cloud storage quota (bytes) for the user's recording library. 0 for Free
   * (recordings are local-only); 100GB + active top-ups for Premium. Sourced
   * from the User read model (`StorageLimitBytes`).
   */
  storageLimitBytes?: number;
  /** Bytes of the quota currently consumed by stored recordings. */
  storageBytesUsed?: number;
  /**
   * Recording settings (wiki/feature-recording-storage.md §"Retention — a user
   * setting"). All four are surfaced by whoami so the profile settings section
   * and the post-take upload paths read them without a second round-trip.
   */
  /**
   * Premium Auto Save: every completed take auto-materialises + uploads to the
   * Library. Default OFF; defaulted ON on Premium grant, OFF on downgrade.
   * Premium-only — drives the take-end auto-save trigger + the settings toggle.
   * See wiki/feature-practice-persistence.md §"Auto Save (Premium setting)".
   */
  autoSave?: boolean;
  /** Mastery-plan practice-reminder emails turned off; they default on. */
  practiceRemindersOff?: boolean;
  /** Auto-delete stored recordings after the keep window. Default OFF. */
  autoDeleteEnabled?: boolean;
  /** Keep-for-N-days window when auto-delete is on (1–3650). Default 90. */
  retentionKeepDays?: number;
  /** The chosen FE video-quality tier key. Defaults to the generated
   *  `DefaultRecordingQuality` (backend `UserDefaults.RecordingQuality`). */
  defaultRecordingQuality?: string;
  /**
   * Active Premium subscription entitlement — true when the server-side
   * HasActivePremium check passes (backend Models/Billing/PremiumEntitlement.cs).
   * That rule is STATUS-first, not date-first: `Active` and `PastDue` are
   * entitled on the strength of the status alone, because `subscriptionEndsAt`
   * is a cached copy of processor state that goes stale by design (a renewal
   * webhook can arrive long after the period boundary). Only two cases still
   * lapse on the date — a `Cancelled` cancel-at-period-end, and an admin
   * hand-grant with no processor subscription behind it. So an elapsed
   * `subscriptionEndsAt` next to `isPremium: true` is normal, never a bug: never
   * re-derive entitlement from the date on the client. Sourced directly from the
   * whoami response; the `isPremium` store reads this field, not the roles array.
   */
  isPremium?: boolean;
  /** When the signup credit account started waiting to open; null once open. */
  creditAccountPendingSince?: string | null;
  /** When a purchase paid before sign-up started waiting to be applied; null when none waits. */
  purchasePendingSince?: string | null;
  /** IANA zone dates render in; null until detected or chosen. */
  timeZone?: string | null;
  /** The cell this account is being moved to while its home cell holds it read-only. */
  movingTo?: string | null;
  /**
   * Set only while an impersonation session is live — in which case every OTHER
   * field on this object is the TARGET's, and this names the admin.
   */
  impersonation?: ImpersonationStatus | null;
};

export type EmailRequestResponse = { requestId: string };

// Identity-vs-authorization split (wiki/feature-access-model.md §1, §6):
// "whoami returned a profile" does not mean "signed in". A Guest is not logged
// in but IS authorised (the role-ladder floor). The status is THREE-way because
// the client genuinely cannot know the visitor until whoami settles, and
// asserting either answer for that first frame is a lie:
//   • loading       — the initial value; the first whoami has not settled. Every
//                      consumer must say what it renders/does here — it is
//                      neither guest nor authenticated. It always exits: whoami
//                      resolves it, and every failure path floors it to `guest`
//                      (`refresh()`'s finally, `bootstrapAuth()`, `setGuest()`).
//   • authenticated — whoami succeeded AND the principal has the `User` role
//                      (or any role that implies it: Admin/SuperAdmin).
//                      A claimed account: account chrome + the read channel.
//   • guest         — every settled not-logged-in visitor. The free-tier floor:
//                      free offline (Web LLM) generation + a small online
//                      allowance (3 free online LLM generations / the daily
//                      quota). May carry a `CurrentUser` payload (a minted
//                      Guest-role identity) or none. No account surfaces —
//                      those are meaningless for a Guest.
type AuthState =
  // `user?: undefined` keeps `$auth.user` readable across the union — there is
  // simply no identity to read while loading.
  | { status: 'loading'; user?: undefined }
  | { status: 'guest'; user?: CurrentUser }
  | { status: 'authenticated'; user: CurrentUser };

const internal = writable<AuthState>({ status: 'loading' });

export const auth: Readable<AuthState> = { subscribe: internal.subscribe };

// An impersonating admin keeps their own device's zone, never the target's.
internal.subscribe((s) =>
  setRecordedTimeZone(s.status === 'authenticated' && !s.user.impersonation ? s.user.timeZone : null),
);

/**
 * True when the whoami payload represents a claimed account — i.e. carries the
 * `User` role. Because the role implication graph is `Admin ⇒ User` (etc.),
 * any Admin/SuperAdmin principal also carries `User`, so this single check
 * covers every claimed tier. A Guest-only payload (just `Guest`) is false.
 * The backstop `roles` array is always present on `CurrentUser`.
 */
export function hasUserRole(user: Pick<CurrentUser, 'roles'>): boolean {
  return Array.isArray(user.roles) && user.roles.includes('User');
}

/**
 * Maps a successful whoami payload onto the resolved auth status. A claimed
 * account (has `User`) ⇒ authenticated; a Guest-only identity ⇒ guest.
 */
export function statusForUser(user: CurrentUser): 'authenticated' | 'guest' {
  return hasUserRole(user) ? 'authenticated' : 'guest';
}

/**
 * Role-derived helpers the nav / empty-states consume so they never re-derive
 * the role rules inline. `isUser` is true for any claimed account (incl.
 * Admin by implication); `isGuest` is the soft-identity floor; `isPremium`
 * is the subscription entitlement check (read from the `isPremium` field on
 * CurrentUser — NOT from the roles array; Premium is an entitlement, not a role).
 *
 * All four are FALSE while the status is `loading` — nothing is known yet. A
 * consumer that must distinguish "not X" from "not known yet" reads
 * `$auth.status === 'loading'` instead of negating one of these.
 */
export const isUser: Readable<boolean> = derived(internal, ($a) =>
  $a.user ? hasUserRole($a.user) : false,
);

export const isGuest: Readable<boolean> = derived(internal, ($a) => $a.status === 'guest');

/**
 * Whether this session may list the device's cached takes. They belong to whoever
 * recorded them in this browser, so an admin — as themselves or acting as someone —
 * never sees them (wiki/feature-local-recording-cache.md §"Privacy + telemetry").
 * False while loading: the identity is not known yet.
 */
export function showsDeviceTakes(state: AuthState): boolean {
  return (
    state.status !== 'loading' &&
    !state.user?.impersonation &&
    !(state.user?.roles.includes('Admin') ?? false)
  );
}

/** True only for a settled claimed account. */
export const isAuthenticated: Readable<boolean> = derived(
  internal,
  ($a) => $a.status === 'authenticated',
);

/**
 * True when the current user holds an active Premium subscription entitlement.
 * Reads the `isPremium` field from the whoami response (computed server-side
 * via `UserState.HasActivePremium`) — not the roles array. Premium is a
 * subscription entitlement, not a role.
 */
export const isPremium: Readable<boolean> = derived(internal, ($a) =>
  $a.user ? ($a.user.isPremium ?? false) : false,
);

/** Synchronous read of the current status — handy in non-reactive code. */
export function currentStatus(): AuthState['status'] {
  return get(internal).status;
}

/**
 * Requests a magic-link sign-in email. `name` is the optional display name the
 * buyer confirmed during a checkout-claim — threaded so the backend sets it on
 * the new account at claim time and the `NameDialog` never fires
 * (wiki/feature-checkout-claim.md AC-CC-4). Omitted on the plain login path.
 * Throws `ApiError` on failure — the caller catches for inline UX.
 */
export async function requestEmailLink(
  email: string,
  name?: string | null,
): Promise<EmailRequestResponse> {
  const trimmedName = name?.trim();
  // No token when reCAPTCHA is blocked: the server decides (Development's fake check passes it).
  const captchaToken = await recaptchaToken(SignInBotCheckAction).catch(() => undefined);
  return api.post<EmailRequestResponse>('/auth/email/request', {
    email,
    culture: wireCulture(activeCulture()),
    ...(trimmedName ? { name: trimmedName } : {}),
    ...(captchaToken ? { captchaToken } : {}),
  });
}

/**
 * Records a UI-language switch on the signed-in user so their emails follow it.
 * Fire-and-forget; a guest's choice stays local, and an admin impersonating must
 * not change the target's emails.
 */
export function persistCulture(culture: Culture): void {
  const state = get(internal);
  if (state.status !== 'authenticated' || state.user.impersonation) return;
  api
    .command(setUserCulture, { id: state.user.id, culture: wireCulture(culture) }, { requiresAuth: true })
    .catch(() => {});
}

// `detected` only fills an empty zone server-side, so it never replaces a choice.
export async function saveTimeZone(timeZone: string, detected = false): Promise<void> {
  const state = get(internal);
  if (state.status !== 'authenticated' || state.user.impersonation) return;
  await api.command(
    setUserTimeZone,
    { id: state.user.id, timeZone, detected },
    { requiresAuth: true, ...(detected ? { toast: false as const } : {}) },
  );
  patchCurrentUser({ timeZone });
}

// Cross-tab session signal (wiki/feature-checkout-claim.md AC-CC-6).
//
// When ANY tab establishes/claims a session (magic-link click exchange, OTP
// verify, OAuth return), it posts on this single-purpose `BroadcastChannel`.
// Every open tab listens and refetches `whoami` — so the ORIGINAL guest tab
// flips to authenticated live without a SignalR push (the claim lands on the
// *email-derived* identity, which the guest tab's self-watch can't see). The
// `jwt` cookie is HttpOnly + shared across tabs, so a plain refetch is all
// that's needed. The listener only calls `refresh()` (which does NOT
// re-broadcast), so there is no ping-pong between tabs.
const AUTH_BROADCAST_CHANNEL = 'auth';
const AUTH_SESSION_ESTABLISHED = 'session-established';

function authChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
}

/**
 * Announce to every other open tab that this tab just established/claimed a
 * session, so they refetch `whoami` and flip to authenticated live (AC-CC-11).
 */
export function broadcastSessionEstablished(): void {
  const channel = authChannel();
  if (!channel) return;
  try {
    channel.postMessage(AUTH_SESSION_ESTABLISHED);
  } finally {
    channel.close();
  }
}

// True while a refresh() is running in response to a cross-tab signal — so that
// refresh does NOT re-broadcast (the originating tab already announced the
// session). Prevents any cross-tab broadcast amplification.
let suppressNextBroadcast = false;

/**
 * Start listening for cross-tab session signals. On each one, refetch `whoami`
 * so this tab picks up a session another tab just established (AC-CC-11). The
 * triggered refresh does NOT re-broadcast (the originating tab already did), so
 * there is no cross-tab amplification. Returns a teardown that closes the
 * channel; a no-op where `BroadcastChannel` is unavailable (SSR / old browsers).
 */
export function initAuthBroadcast(): () => void {
  const channel = authChannel();
  if (!channel) return () => {};
  channel.onmessage = (event) => {
    if (event.data === AUTH_SESSION_ESTABLISHED) {
      suppressNextBroadcast = true;
      void refresh();
    }
  };
  return () => channel.close();
}

type SignInResponse = OperationResponse | CellHandoffResponse | undefined;

// The account's home is another cell, which finishes this sign-in. The promise never settles
// because the page is leaving.
function followHandoff(response: SignInResponse, returnUrl?: string): Promise<never> | undefined {
  if (!response || !('handoffUrl' in response)) return undefined;
  const target = new URL(response.handoffUrl);
  if (returnUrl) target.searchParams.set('returnUrl', returnUrl);
  location.assign(target.href);
  return new Promise<never>(() => {});
}

/** The inbox is proven, but the cell cannot tell where the account lives or reach it. */
export function isRegionUnavailable(e: ApiError): boolean {
  return e.problem?.reason === 'region-unavailable';
}

/** Throws `ApiError` on failure — the caller catches for inline UX. */
export async function exchangeClickToken(clickToken: string, returnUrl?: string): Promise<void> {
  const response = await api.post<SignInResponse>('/auth/exchange', { clickToken });
  await followHandoff(response, returnUrl);
  await refresh();
}

/**
 * Verifies a 6-digit OTP typed on the "check your email" screen. The backend
 * (`POST /api/auth/email/verify-otp`) signs the user in directly on the
 * response (sets the `vd_session` cookie). Throws `ApiError` on a 400; the
 * caller reads `error.problem?.reason`
 * (`invalid` | `expired` | `too-many-attempts`) to pick inline copy.
 * See wiki/feature-magic-link-auth.md AC11.5.
 */
export async function verifyEmailOtp(requestId: string, otp: string, returnUrl?: string): Promise<void> {
  // The session cookie lands on this response. The caller refetches whoami
  // via `refresh()`; the guest→authenticated transition there broadcasts the
  // cross-tab signal (AC-CC-11), so no explicit broadcast is needed here.
  const response = await api.post<SignInResponse>('/auth/email/verify-otp', { requestId, otp });
  await followHandoff(response, returnUrl);
}

export async function logout(): Promise<void> {
  // Best-effort: a failed logout still drops the FE to guest. Catch so the
  // network ApiError doesn't toast on a flow whose whole point is leaving.
  // Each plane's sign-out clears only its own session cookie.
  const path = ON_ADMIN_HOST ? '/admin/logout' : '/user/logout';
  try {
    await api.post<unknown>(path, {});
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
  }
  internal.set({ status: 'guest' });
  await ensureGuestJwt();
}

/**
 * Immediate, irreversible self-serve account deletion
 * (wiki/feature-account-deletion.md). Calls `DELETE /api/user` — the backend
 * cancels any Stripe subscription, deletes the user's recordings + account, and
 * clears the session cookies on the response. On success the auth store drops to
 * anonymous so the chrome reflects the signed-out state at once.
 *
 * Unlike `logout()` this is NOT best-effort: a failure must surface to the
 * caller (so the danger-zone UI can toast the error and keep the account),
 * because a silent failure here would wrongly tell the user they were deleted.
 */
export async function deleteAccount(): Promise<void> {
  await api.del<unknown>('/user');
  internal.set({ status: 'guest' });
  await ensureGuestJwt();
}

/**
 * Drop the store to the Guest floor (the not-logged-in state). Used by the boot
 * recovery path when the backend is unreachable so the chrome settles instead of
 * hanging unresolved.
 */
export function setGuest(): void {
  internal.set({ status: 'guest' });
}

/**
 * Ensures a Guest JWT cookie exists before any `/api/query` read fires.
 *
 * Calls `POST /api/auth/guest` — a stateless, idempotent endpoint that mints
 * the Guest JWT from the `vd_anon` UUID without upsetting any aggregate. The
 * endpoint returns 204 immediately when a valid JWT is already present, so
 * calling this every guest load is safe.
 *
 * **Resilient by design:** any network failure or non-2xx response is swallowed
 * here — `POST /api/auth/guest` failing must not block render. Callers chain
 * `.finally(() => setGuest())` so the chrome settles regardless. A missing Guest
 * JWT only means that the first `/api/query` call would get 401 rather than
 * returning empty data — the query-error path handles that gracefully.
 *
 * See `wiki/feature-access-model.md` §4 and
 * `wiki/feature-frontend-cqrs-dataflow.md` §1a.
 */
/**
 * True when the JS-readable `vd_session` indicator cookie is present — the
 * boot-time signal that a claimed account established a session (magic-link /
 * OAuth / purchase; a Guest JWT never sets it). `hooks.client.ts` reads the same
 * cookie to decide whether to call whoami at all; `refresh()` reads it to detect
 * the auth-recovery case (indicator present but whoami resolves to guest →
 * backend cleared a stale claimed token). SSR-safe: no `document` ⇒ false.
 */
export function hasSessionIndicatorCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((c) => c.trim().startsWith('vd_session='));
}

export async function ensureGuestJwt(): Promise<void> {
  if (ON_ADMIN_HOST) return;
  try {
    // 204 No Content — no response body to parse. The jwt cookie lands on the
    // response; the browser stores it automatically (HttpOnly, credentials:include).
    await api.post<unknown>('/auth/guest', {}, { toast: false });
  } catch {
    // Swallow all errors — a bootstrap failure must not block render. The guest
    // chrome still settles via the `.finally(() => setGuest())` in hooks.client.ts.
  }
}

// A 401 burst shares one in-flight recovery per branch.
let accessRefreshInflight: Promise<boolean> | null = null;
const REFRESH_LOCK = 'vd-refresh';
let guestRemintInflight: Promise<boolean> | null = null;

// The client's 401 hook: claimed → rotate via /auth/refresh (false = session ended, and it
// rejects with the 503 while a move holds the rotation, which says nothing about the session);
// guest → re-mint the Guest JWT, never /auth/refresh (a guest has no refresh token).
export async function refreshAccessToken(): Promise<boolean> {
  // The admin plane has no guest and no refresh token: Access re-proves the identity.
  if (ON_ADMIN_HOST) {
    if (accessRefreshInflight) return accessRefreshInflight;
    accessRefreshInflight = openAdminSession()
      .catch((e: unknown) => {
        if (e instanceof ApiError) return false;
        throw e;
      })
      .finally(() => {
        accessRefreshInflight = null;
      });
    return accessRefreshInflight;
  }
  // Guest: re-mint the Guest JWT instead of hitting /auth/refresh (a sure 401).
  if (!hasSessionIndicatorCookie()) {
    if (guestRemintInflight) return guestRemintInflight;
    guestRemintInflight = (async () => {
      try {
        // 204 No Content — the fresh guest `jwt` cookie lands on the response.
        // `{ toast: false }`: a guest re-mint is an invisible recovery step.
        await api.post<unknown>('/auth/guest', {}, { toast: false });
        return true;
      } catch (e) {
        if (e instanceof ApiError) {
          return false;
        }
        throw e;
      } finally {
        guestRemintInflight = null;
      }
    })();
    return guestRemintInflight;
  }

  if (accessRefreshInflight) return accessRefreshInflight;
  accessRefreshInflight = refreshAcrossTabs().finally(() => {
    accessRefreshInflight = null;
  });
  return accessRefreshInflight;
}

// Tabs share one cookie jar, so only one may rotate `vd_refresh` at a time. A tab that
// finds another mid-refresh waits it out and retries on the cookies that tab set.
// Without Web Locks the server's rotation grace window absorbs the race instead.
function refreshAcrossTabs(origin?: string): Promise<boolean> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return postRefresh(origin);
  return locks.request(REFRESH_LOCK, { ifAvailable: true }, (lock) =>
    lock ? postRefresh(origin) : locks.request(REFRESH_LOCK, () => throwIfRefreshFrozen()),
  );
}

// The tab that held the lock tells the ones that waited on it, which would otherwise retry on
// cookies nothing rotated, read the 401 as a sign-out, and drop to guest.
const REFRESH_FROZEN_KEY = 'voicedojo:refresh-frozen';

function recordRefreshFrozen(e: ApiError | null): void {
  try {
    if (e) localStorage.setItem(REFRESH_FROZEN_KEY, JSON.stringify({ until: Date.now() + (e.retryAfterSeconds ?? 30) * 1000, problem: e.problem }));
    else localStorage.removeItem(REFRESH_FROZEN_KEY);
  } catch {
    // Without storage only a second tab's wait is lost, and its next load recovers.
  }
}

function throwIfRefreshFrozen(): true {
  let frozen: { until: number; problem: ApiError['problem'] } | null = null;
  try {
    frozen = JSON.parse(localStorage.getItem(REFRESH_FROZEN_KEY) ?? 'null');
  } catch {
    return true;
  }
  if (!frozen || frozen.until <= Date.now()) return true;
  const seconds = Math.ceil((frozen.until - Date.now()) / 1000);
  noteAccountMoving(typeof frozen.problem?.movingTo === 'string' ? frozen.problem.movingTo : undefined, seconds);
  throw new ApiError(503, t(SystemKeys.Messages.AccountMoving), frozen.problem, true, seconds);
}

async function postRefresh(origin?: string): Promise<boolean> {
  try {
    // 204 No Content on success — the rotated `jwt` + `vd_refresh` cookies land
    // on the response. `{ toast: false }`: a refused refresh is the expected
    // "session ended" path, not a user-facing error.
    await api.post<unknown>('/auth/refresh', undefined, { toast: false, ...(origin ? { origin } : {}) });
    if (!origin) recordRefreshFrozen(null);
    return true;
  } catch (e) {
    // Dialling another cell, this answer says nothing about the session this page holds.
    if (isAccountMoving(e) && !origin) {
      recordRefreshFrozen(e);
      throw e;
    }
    if (e instanceof ApiError) {
      return false;
    }
    throw e;
  }
}

let crossing: Promise<boolean> | null = null;

let crossingHeld: (() => boolean) | null = null;

// A reload would end a take in progress, so crossing waits for a request made after it.
export function setCellCrossingHold(fn: (() => boolean) | null): void {
  crossingHeld = fn;
}

// A 421 names the cell that owns this session. This cannot loop: a cell the page already dials
// or one outside the list stops here, and only a successful refresh reloads, after which the
// token names the cell the reload dials. A failed refresh leaves vd_home for the next page load
// and lets the caller see the 421.
function crossToOwningCell(cell: string): Promise<boolean> {
  crossing ??= (async () => {
    const origin = cellOrigin(cell);
    if (ON_ADMIN_HOST || !origin || origin === apiOrigin() || crossingHeld?.()) return false;
    writeHomeCookie(cell, origin);
    if (!(await refreshAcrossTabs(origin))) return false;
    markCellCrossing();
    location.reload();
    return true;
  })().then((crossed) => {
    if (!crossed) crossing = null;
    return crossed;
  });
  return crossing;
}

setCrossToCell(crossToOwningCell);
setAccountMovingListener(noteAccountMoving);

// Wire the refresh-on-401 baseline into the shared API client at module load: any
// 401 from a normal call recovers once (claimed → re-mint the access token; guest
// → re-mint the Guest JWT) and retries. The client excludes the auth endpoints
// themselves from the retry so this never recurses.
setRefreshOn401(refreshAccessToken);

// Wire the resolved-guest gate into the shared API client at module load: a
// `requiresAuth` call (a claimed-only User+ endpoint) is short-circuited with no
// network round-trip once auth has RESOLVED to a guest. Never short-circuits
// before resolution, so a returning claimed user mid-resolution still fires.
setGuestGate(() => get(internal).status === 'guest');

/**
 * Patch fields on the currently-resolved user in place (without a whoami
 * round-trip). Used after a settings save (e.g. recording settings) so the
 * chrome reflects the new values immediately. A no-op when no user is resolved.
 */
export function patchCurrentUser(patch: Partial<CurrentUser>): void {
  internal.update((s) => {
    if (!s.user) return s;
    return { ...s, user: { ...s.user, ...patch } };
  });
}

// In-flight de-dup for `refresh`. At boot two callers race: `hooks.client.ts`
// fires `refresh()` on hydration when the `vd_session` cookie is present, and
// the root `+layout.svelte` `onMount` fires it again to resolve whoami globally.
// Without this guard that's two `GET /api/user/whoami` round-trips on every
// load. While a refresh is in flight every concurrent caller shares the SAME
// promise; the slot clears when it settles so a later, intentional re-sync
// (e.g. post-exchange) always hits the server afresh.
let refreshInflight: Promise<void> | null = null;

// One-shot memoized auth bootstrap. The TWO boot entry points — `hooks.client.ts`
// (module-init on hydration) and the root `+layout.svelte` `onMount` — both await
// THIS, so the initial identity resolution (the `whoami` round-trip) happens
// EXACTLY ONCE regardless of which fires first or how staggered they are. The
// `refreshInflight` guard inside `refresh()` only collapses *concurrent* callers
// and clears on settle, so two STAGGERED boot calls (module-init vs onMount) each
// ran a fresh whoami — this memo is the fix.
//
// Deliberately NEVER cleared: a later boot-intent caller reuses the already-
// resolved promise instead of re-hitting whoami. Only the two boot entry points
// call this. Every INTENTIONAL re-sync (post-login / exchange / OTP / OAuth
// return, the backend-recover re-fire, the cross-tab broadcast refetch) calls
// `refresh()` directly so it hits the server afresh — those must never be
// swallowed by this memo.
let bootInflight: Promise<void> | null = null;

export function bootstrapAuth(): Promise<void> {
  if (bootInflight) return bootInflight;
  bootInflight = (async () => {
    // A claimed account always carries the JS-readable `vd_session` indicator
    // cookie (a guest never does). Branch on it the way the two boot entry
    // points did separately:
    //   • indicator present → resolve identity via whoami (`refresh()`), which
    //     also runs the auth-recovery re-mint when a stale claimed token now
    //     resolves to guest.
    //   • no indicator (guest) → eagerly mint the Guest JWT first so the very
    //     first `POST /api/query` read is accepted, THEN resolve identity via
    //     whoami exactly as the layout `onMount` did. `setGuest()` is the floor
    //     so the chrome settles even if whoami never resolves cleanly.
    if (hasSessionIndicatorCookie()) {
      await refresh();
    } else {
      await ensureGuestJwt();
      try {
        await refresh();
      } finally {
        // Floor: if `refresh()` left the store in `loading` (e.g. it threw a
        // non-ApiError before settling), drop to the Guest floor so the nav
        // doesn't hang on the neutral pre-resolution paint.
        if (get(internal).status === 'loading') setGuest();
      }
    }
  })();
  return bootInflight;
}

setAccountMoveRecheck(() => refresh());

export async function refresh(): Promise<void> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    // whoami's 401/404 is the expected "not signed in" path — caught here so it
    // never toasts. Other failures are logged but also don't toast (boot path).
    try {
      // The guards below ("did the identity genuinely change") treat the boot
      // frame as the guest floor — that is what the visitor was being served
      // while whoami was in flight.
      const settledStatus = get(internal).status;
      const previousStatus = settledStatus === 'loading' ? 'guest' : settledStatus;
      // A claimed account always carries the JS-readable `vd_session` indicator
      // cookie (a guest never does — see GuestBootstrapEndpoint). Capture it
      // BEFORE the request: if the backend's auth-recovery clears the cookies on
      // this very response, `document.cookie` would no longer show it afterward.
      const hadSessionIndicator = hasSessionIndicatorCookie();
      const user = await api.get<CurrentUser>('/user/whoami');
      // A successful whoami does not imply "signed in" — gate on role
      // (wiki/feature-access-model.md §1/§6). A Guest-role identity resolves to
      // `guest` (carrying its payload); a claimed account (User+, incl.
      // Premium/Admin by implication) resolves to `authenticated`.
      const nextStatus = statusForUser(user);
      internal.set({ status: nextStatus, user });
      if (user.movingTo) noteAccountMoving(user.movingTo);
      else noteAccountNotMoving();
      if (nextStatus === 'authenticated' && !user.impersonation && !user.timeZone) {
        saveTimeZone(deviceTimeZone(), true).catch(() => {});
      }

      // Auth-recovery re-bootstrap (wiki/feature-access-model.md §6). When this
      // tab held a claimed identity (in-memory `authenticated`, or — across a
      // hard reload — the `vd_session` indicator cookie) but whoami now resolves
      // to guest, the backend has detected a stale claimed token (its `User`
      // aggregate was destroyed/deleted, or the account was deactivated) and
      // CLEARED the session cookies on the whoami response. The HttpOnly `jwt`
      // cookie is now gone, so the next `POST /api/query` read would 401 —
      // re-mint a fresh Guest JWT (stateless, idempotent) so the app continues as
      // a clean guest (guest quota, no premium). A normal guest load never enters
      // this branch (no prior claimed identity → no re-bootstrap).
      if (nextStatus === 'guest' && (previousStatus === 'authenticated' || hadSessionIndicator)) {
        await ensureGuestJwt();
      }

      // Cross-tab session signal (wiki/feature-checkout-claim.md AC-CC-6): on
      // a guest→authenticated transition (this tab just established/claimed a
      // session via exchange / OTP / OAuth return), announce it so every other
      // open tab refetches whoami and flips live. A refresh triggered BY an
      // incoming signal does not re-broadcast (`suppressNextBroadcast`), so
      // there is no cross-tab amplification.
      const wasSuppressed = suppressNextBroadcast;
      suppressNextBroadcast = false;
      if (!wasSuppressed && previousStatus !== 'authenticated' && nextStatus === 'authenticated') {
        broadcastSessionEstablished();
      }
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
        internal.set({ status: 'guest' });
        noteAccountNotMoving();
      } else if (isAccountMoving(e)) {
        // The session is intact and its next check is already scheduled.
      } else if (e instanceof ApiError) {
        console.warn('[auth] whoami failed:', e.status, e.message);
      } else {
        throw e;
      }
    } finally {
      // The first whoami has settled — never leave the store in `loading`. A
      // failure without a clean 401/404 (backend down, offline) resolves to the
      // Guest floor, so the nav always swaps off the pre-resolution paint. A move
      // holding the session is the exception: a signed-in user must not read as a
      // guest, and the move's recheck settles this when it ends.
      if (get(internal).status === 'loading' && !get(accountMove)) internal.set({ status: 'guest' });
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}
